import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { Phone, MapPin, Clock, Send, CheckCircle, AlertCircle, Mail } from "lucide-react";
import { useForm } from "react-hook-form";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: data.name,
        phone: data.phone,
        message: data.message || "",
      });
      setIsSubmitted(true);
      toast.success("Wiadomość wysłana!", {
        description: "Skontaktujemy się z Tobą wkrótce.",
      });
      reset();
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Wystąpił błąd", {
        description: "Spróbuj ponownie lub zadzwoń do nas.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Telefon",
      value: "608 490 104",
      href: "tel:608490104",
      description: "Zadzwoń i umów się na kurs",
    },
    {
      icon: Mail,
      title: "Email",
      value: "fsmreczak@interia.pl",
      href: "mailto:fsmreczak@interia.pl",
      description: "Napisz do nas",
    },
    {
      icon: MapPin,
      title: "Lokalizacja",
      value: "Niedźwiedź 300, 34-735",
      description: "Egzamin w MORD Nowy Targ",
    },
    {
      icon: Clock,
      title: "Godziny pracy biura",
      value: "Pon-Sob: 8:00-20:00",
      description: "Elastyczne terminy jazd",
    },
  ];

  return (
    <section
      id="kontakt"
      className="py-20 lg:py-32 bg-slate-50"
      data-testid="contact-section"
      ref={ref}
    >
      <div className="section-container">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left Side - Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-block bg-yellow-400/20 text-slate-900 px-4 py-2 rounded-sm font-heading font-bold text-sm tracking-wider mb-6">
              KONTAKT
            </div>
            <h2
              className="font-heading font-bold text-4xl lg:text-5xl text-slate-900 tracking-tight mb-6"
              data-testid="contact-title"
            >
              SKONTAKTUJ SIĘ
              <span className="block text-yellow-500">Z NAMI</span>
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-10">
              Masz pytania dotyczące kursu? Chcesz się zapisać? Zadzwoń lub wypełnij formularz, 
              a my odezwiemy się do Ciebie jak najszybciej.
            </p>

            {/* Contact Info Cards */}
            <div className="space-y-4" data-testid="contact-info">
              {contactInfo.map((info, index) => (
                <motion.div
                  key={info.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
                  className="flex items-start gap-4 p-5 bg-white border-2 border-slate-100 rounded-sm hover:border-yellow-400 transition-colors group"
                  data-testid={`contact-info-${index}`}
                >
                  <div className="w-12 h-12 bg-slate-900 rounded-sm flex items-center justify-center flex-shrink-0 group-hover:bg-yellow-400 transition-colors">
                    <info.icon className="w-6 h-6 text-yellow-400 group-hover:text-slate-900 transition-colors" />
                  </div>
                  <div>
                    <div className="font-heading font-bold text-slate-900 tracking-tight mb-1">
                      {info.title}
                    </div>
                    {info.href ? (
                      <a
                        href={info.href}
                        className="font-heading font-bold text-xl text-yellow-500 hover:text-yellow-600 transition-colors"
                      >
                        {info.value}
                      </a>
                    ) : (
                      <div className="font-heading font-bold text-xl text-slate-900">
                        {info.value}
                      </div>
                    )}
                    <div className="text-sm text-slate-500 mt-1">{info.description}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Side - Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-white border-2 border-slate-100 p-8 lg:p-10 rounded-sm shadow-sm">
              <h3 className="font-heading font-bold text-2xl text-slate-900 tracking-tight mb-2">
                Formularz kontaktowy
              </h3>
              <p className="text-slate-500 mb-8">
                Wypełnij formularz, a skontaktujemy się z Tobą.
              </p>

              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12"
                  data-testid="contact-success"
                >
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h4 className="font-heading font-bold text-xl text-slate-900 mb-2">
                    Dziękujemy!
                  </h4>
                  <p className="text-slate-600">
                    Twoja wiadomość została wysłana. Odezwiemy się wkrótce.
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit(onSubmit)} data-testid="contact-form">
                  <div className="space-y-6">
                    {/* Name Field */}
                    <div>
                      <Label htmlFor="name" className="font-heading font-semibold text-slate-700 tracking-wide mb-2 block">
                        Imię i nazwisko *
                      </Label>
                      <Input
                        id="name"
                        type="text"
                        placeholder="Jan Kowalski"
                        className={`w-full px-4 py-3 border-2 rounded-sm transition-colors ${
                          errors.name ? "border-red-400" : "border-slate-200 focus:border-yellow-400"
                        }`}
                        {...register("name", {
                          required: "Imię i nazwisko jest wymagane",
                          minLength: { value: 2, message: "Minimum 2 znaki" },
                        })}
                        data-testid="contact-name-input"
                      />
                      {errors.name && (
                        <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.name.message}
                        </p>
                      )}
                    </div>

                    {/* Phone Field */}
                    <div>
                      <Label htmlFor="phone" className="font-heading font-semibold text-slate-700 tracking-wide mb-2 block">
                        Numer telefonu *
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="600 000 000"
                        className={`w-full px-4 py-3 border-2 rounded-sm transition-colors ${
                          errors.phone ? "border-red-400" : "border-slate-200 focus:border-yellow-400"
                        }`}
                        {...register("phone", {
                          required: "Numer telefonu jest wymagany",
                          pattern: {
                            value: /^[0-9\s+-]{9,15}$/,
                            message: "Nieprawidłowy numer telefonu",
                          },
                        })}
                        data-testid="contact-phone-input"
                      />
                      {errors.phone && (
                        <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.phone.message}
                        </p>
                      )}
                    </div>

                    {/* Message Field */}
                    <div>
                      <Label htmlFor="message" className="font-heading font-semibold text-slate-700 tracking-wide mb-2 block">
                        Wiadomość (opcjonalnie)
                      </Label>
                      <Textarea
                        id="message"
                        rows={4}
                        placeholder="Napisz wiadomość..."
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-sm focus:border-yellow-400 transition-colors resize-none"
                        {...register("message")}
                        data-testid="contact-message-input"
                      />
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="contact-submit-button"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
                          Wysyłanie...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Wyślij wiadomość
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
